import { createSelector } from 'reselect';

import type { RootState } from 'config/store/reducers';
import type { Entity } from 'modules/entity/types';
import type { KeyDepositCustomMessageTypeEnum } from 'types/Dealer';
import type { MayBe } from 'types/common';
import { getEntity } from 'modules/entity/selectors';
import type { AppointmentTask, AppointmentTaskTypeEnum } from './types';

import { APPOINTMENT_TASK_TYPES, CUSTOM_MESSAGE_TYPES } from './constants';

const getState = (state: RootState) => state.appointment;

const getUI = createSelector(getState, (state) => state.ui);

const getGeneralUI = createSelector(getUI, (ui) => ui.general);
const getDropOffUI = createSelector(getUI, (ui) => ui.dropOff);
const getPickUpUI = createSelector(getUI, (ui) => ui.pickUp);

export const isLoadingAcceptRentalDocuments = createSelector(
  getGeneralUI,
  (general) => general.acceptRentalDocuments.isLoading
);
export const isLoadingAcceptRepairOrder = createSelector(
  getGeneralUI,
  (general) => general.acceptRepairOrder.isLoading
);
export const isLoadingConfirmClientDocuments = createSelector(
  getGeneralUI,
  (general) => general.confirmClientDocuments.isLoading
);
export const isLoadingPayBilling = createSelector(getGeneralUI, (general) => general.payBilling.isLoading);
export const isLoadingReportIssues = createSelector(getGeneralUI, (general) => general.reportIssues.isLoading);

export const isLoadingDropOffConfirm = createSelector(getDropOffUI, (dropOff) => dropOff.confirm.isLoading);
export const isLoadingDropOffOpen = createSelector(getDropOffUI, (dropOff) => dropOff.open.isLoading);

export const isLoadingPickUpConfirm = createSelector(getPickUpUI, (pickUp) => pickUp.confirm.isLoading);
export const isLoadingPickUpOpen = createSelector(getPickUpUI, (pickUp) => pickUp.open.isLoading);

export const getForceLiveDrivingLicense = createSelector(getEntity, (entity) => entity.dealer?.forceLiveDrivingLicense);

const hasAppointmentTasks = ({ appointmentTasks = [] }: Entity) => Object.keys(appointmentTasks).length > 0;

const getTaskByType = (appointment: Entity, type: AppointmentTaskTypeEnum): MayBe<AppointmentTask> | null => {
  const { appointmentTasks = [] } = appointment;
  if (hasAppointmentTasks(appointment)) {
    return appointmentTasks.find(({ task }) => task.type === type);
  }
  return null;
};

/**
 * Builds a { has, get } selector pair for an appointment task type: `has` reports whether
 * the appointment carries that task at all, `get` always returns a safe, destructurable object.
 */
const buildTaskSelectors = (type: AppointmentTaskTypeEnum) => {
  const getRawTask = createSelector(getEntity, (appointment) => getTaskByType(appointment, type));
  return {
    hasTask: createSelector(getRawTask, Boolean),
    getTask: createSelector(getRawTask, (task) => task ?? ({} as AppointmentTask)),
  };
};

const keyDepositSelectors = buildTaskSelectors(APPOINTMENT_TASK_TYPES.KEY_DEPOSIT);
const keyRetrieveSelectors = buildTaskSelectors(APPOINTMENT_TASK_TYPES.KEY_RETRIEVE);
const repairOrderSelectors = buildTaskSelectors(APPOINTMENT_TASK_TYPES.REPAIR_ORDER);
const billingSelectors = buildTaskSelectors(APPOINTMENT_TASK_TYPES.BILLING);
const rentalCarSelectors = buildTaskSelectors(APPOINTMENT_TASK_TYPES.RENTAL_CAR);
const clientDocumentsSelectors = buildTaskSelectors(APPOINTMENT_TASK_TYPES.DRIVER_DOCUMENTS);

export const hasKeyDepositTask = keyDepositSelectors.hasTask;
export const getKeyDepositTask = keyDepositSelectors.getTask;

export const hasKeyRetrieveTask = keyRetrieveSelectors.hasTask;
export const getKeyRetrieveTask = keyRetrieveSelectors.getTask;

export const hasBooking = createSelector(
  hasKeyDepositTask,
  hasKeyRetrieveTask,
  (hasKeyDeposit, hasKeyRetrieve) => hasKeyDeposit || hasKeyRetrieve
);

export const hasRepairOrderTask = repairOrderSelectors.hasTask;
export const getRepairOrderTask = repairOrderSelectors.getTask;

export const hasBillingTask = billingSelectors.hasTask;
export const getBillingTask = billingSelectors.getTask;

export const hasRentalCarTask = rentalCarSelectors.hasTask;
export const getRentalCarTask = rentalCarSelectors.getTask;

export const getRentalCarVehicleTask = createSelector(getRentalCarTask, (rentalCar) => rentalCar.task ?? {});

export const hasClientDocumentsTask = clientDocumentsSelectors.hasTask;
export const getClientDocumentsTask = clientDocumentsSelectors.getTask;

const getCustomMessageByType = (entity: Entity, type: KeyDepositCustomMessageTypeEnum) =>
  entity?.dealer?.keyDepositCustomMessages?.[type];

export const getVaultAccessCustomMessage = createSelector(getEntity, (entity) =>
  getCustomMessageByType(entity, CUSTOM_MESSAGE_TYPES.VAULT_ACCESS));

export const getClientVehicleDepositCustomMessage = createSelector(getEntity, (entity) =>
  getCustomMessageByType(entity, CUSTOM_MESSAGE_TYPES.CLIENT_VEHICLE_DEPOSIT));

export const getClientVehicleRetrieveCustomMessage = createSelector(getEntity, (entity) =>
  getCustomMessageByType(entity, CUSTOM_MESSAGE_TYPES.CLIENT_VEHICLE_RETRIEVE));

export const getKeyDepositDocuments = createSelector(getKeyDepositTask, (keyDeposit) =>
  (keyDeposit.task?.type === APPOINTMENT_TASK_TYPES.KEY_DEPOSIT ? keyDeposit.task.documents : undefined) ?? []);

export const canClientDocumentsUploadFile = createSelector(
  getClientDocumentsTask,
  ({ confirmUrl, uploadUrl }) => Boolean(confirmUrl && uploadUrl)
);
